/**
 * Multi-Venue State Management Types
 * Supports storing data for multiple venues simultaneously
 */
import { markRaw } from "vue";
import type {
    Venue,
    Level,
    Building,
    Space,
    EdgeDevice,
    EdgeUser,
    Marker,
    Safezone,
    Incident,
    IncidentComment,
    IncidentSafeZone,
    EndUserAssisting,
    IncidentDuplicate,
    BeaconPlacementLocation,
} from "./api";

// ================================================================
// Loading & Error States
// ================================================================

export interface LevelLoadingState {
    devices: boolean;
    markers: boolean;
    spaces: boolean;
    buildings: boolean;
}

export interface LevelErrorState {
    devices: string | null;
    markers: string | null;
    spaces: string | null;
    buildings: string | null;
}

// ================================================================
// Data Structures
// ================================================================

/**
 * Data for a specific level within a venue
 * Note: Users are stored at venue level (VenueData.endUsers), not per-level
 */
export interface LevelData {
    levelId: number;
    devices: EdgeDevice[];
    markers: Marker[];
    spaces: Space[];
    buildings: Building[];
    lastFetched: Date | null;
    loading: LevelLoadingState;
    errors: LevelErrorState;
}

/**
 * Complete data for a venue
 */
export interface VenueData {
    venueId: number;
    venue: Venue | null;
    levels: Level[];
    /** All users in the venue (stored at venue level, filtered by levelId for display) */
    endUsers: EdgeUser[];
    /** Active incidents only (confirmed/unconfirmed) */
    activeIncidents: Incident[];
    /** Comments stored by incidentId */
    incidentComments: Record<string, IncidentComment[]>;
    /**
     * Master safezone list for the venue. Safezones are venue-scoped (level
     * filtering happens client-side via Safezone.levelId) so they live here
     * rather than per-level. Sourced from /safezones, kept in sync via MQTT
     * `safeZones` deltas, and consumed by the map after filtering by level
     * and (when scoped to an incident) by the incident's incidentSafeZones.
     */
    safezones: Safezone[];
    safezonesLastFetched: Date | null;
    /** All spaces for the venue, fetched venue-wide for list views (filter/display). */
    venueSpaces: Space[];
    venueSpacesLastFetched: Date | null;
    levelData: Map<number, LevelData>;
    lastFetched: Date | null;
    usersLastFetched: Date | null;
    loading: {
        venue: boolean;
        levels: boolean;
        incidents: boolean;
        users: boolean;
        safezones: boolean;
        spaces: boolean;
    };
    errors: {
        venue: string | null;
        levels: string | null;
        incidents: string | null;
        users: string | null;
        safezones: string | null;
        spaces: string | null;
    };
}

/**
 * Summary stats for a venue (dashboard display)
 */
export interface VenueSummary {
    venueId: number;
    venueName: string;
    activeIncidentCount: number;
    confirmedIncidentCount: number;
    unconfirmedIncidentCount: number;
    hasShooterIncident: boolean;
    lastActivity: Date | null;
}

/**
 * Delta update targeting a specific venue
 * Principle:
 * - PATCH/update: Merge with existing entity (partial update)
 * - PUT: Replace entire entity (full update)
 * - POST/create: Add new entity
 * - DELETE/delete: Remove entity
 */
export interface VenueDelta {
    /** Message scope - when "incident", all data belongs to the incident(s) */
    scope?:
        | "incident"
        | "location"
        | "notification"
        | "system"
        | "user"
        | "device"
        | "beaconPlacementLocation";
    /** CRUD operation method */
    method?: "POST" | "PUT" | "PATCH" | "DELETE";
    /** Server-side timestamp of the delta update */
    timestamp?: string | number;
    /** Incident updates */
    incidents?: Array<Partial<Incident> & { id: number }>;
    /** Markers */
    markers?: Array<Partial<Marker> & { id: number }>;
    /** Safezones */
    safeZones?: Array<Partial<Safezone> & { id: number }>;
    /** End users (location updates) */
    users?: Array<Partial<EdgeUser> & { id: number }>;
    /** Devices */
    devices?: Array<Partial<EdgeDevice> & { id: string }>;
    /** Incident comments */
    comments?: Array<
        Partial<IncidentComment> & { id: number; incidentId?: number }
    >;
    /** Incident safezones */
    incidentSafeZones?: Array<
        Partial<IncidentSafeZone> & { id: number; incidentId?: number }
    >;
    /** Users assisting */
    endUsersAssisting?: Array<
        Partial<EndUserAssisting> & { id: number; incidentId?: number }
    >;
    /** Duplicate reports */
    incidentDuplicates?: Array<
        Partial<IncidentDuplicate> & { id: number; incidentId?: number }
    >;
    /** Beacon placement locations */
    beaconPlacementLocations?: Array<
        Partial<BeaconPlacementLocation> & { id: number }
    >;
}

// ================================================================
// Factory Functions
// ================================================================

export function createEmptyLevelLoadingState(): LevelLoadingState {
    return {
        devices: false,
        markers: false,
        spaces: false,
        buildings: false,
    };
}

export function createEmptyLevelErrorState(): LevelErrorState {
    return {
        devices: null,
        markers: null,
        spaces: null,
        buildings: null,
    };
}

export function createEmptyLevelData(levelId: number): LevelData {
    return {
        levelId,
        devices: [],
        markers: [],
        spaces: [],
        buildings: [],
        lastFetched: null,
        loading: createEmptyLevelLoadingState(),
        errors: createEmptyLevelErrorState(),
    };
}

export function createEmptyVenueData(venueId: number): VenueData {
    return {
        venueId,
        venue: null,
        levels: [],
        endUsers: markRaw([]),
        activeIncidents: [],
        incidentComments: {},
        safezones: [],
        safezonesLastFetched: null,
        venueSpaces: [],
        venueSpacesLastFetched: null,
        // markRaw prevents Vue from wrapping the Map in a reactive proxy.
        // Without this, Vue's traverse() walks all level data (markers/devices/
        // spaces/buildings) on every MQTT applyDelta() call — consuming
        // ~28% CPU and causing MQTT latency to grow from ~250ms to 15s+ under load.
        levelData: markRaw(new Map()),
        lastFetched: null,
        usersLastFetched: null,
        loading: {
            venue: false,
            levels: false,
            incidents: false,
            users: false,
            safezones: false,
            spaces: false,
        },
        errors: {
            venue: null,
            levels: null,
            incidents: null,
            users: null,
            safezones: null,
            spaces: null,
        },
    };
}
